import { makeStyles } from '@material-ui/core/styles';
import { Button, TextField } from '@material-ui/core';
import { useState } from 'react';
import { createPaymentQuickSearchFilter } from 'src/lib/QueryBuilderHelper';
import {
  getEntitiesAction,
  setFilters,
  setQuickSearch,
} from 'src/redux/actions/querybuilderActions';
import { ENTITIES, QuerbuilderPayloadFilters } from 'src/models/QuerybuilderModel';
import { useSelector } from 'react-redux';
import { Store, useAppDispatch } from 'src/redux/Store';
import { FORM, FORM_STATUS } from 'src/constants/Form';
import { resetSelections } from 'src/redux/actions/selectionActions';
import { t } from 'src/lib/language';
// eslint-disable-next-line import/no-extraneous-dependencies
import moment from 'moment-timezone-all';
import FormDatePicker from 'src/components/Control/FormControls/FormDatePicker';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import { DateType, getDateValue } from 'src/lib/DateHelper';
import { INVOICE_STATUS } from 'src/constants/Invoices';
import { MetadataType } from 'src/components/Control/MetadataSelector';
import { PaymentQuickSearch, QuickSearchPayload } from 'src/models/QuickSearchModel';
import PaymentStatusSelector from 'src/components/Control/PaymentStatusSelector';
import { PAYMENT_STATUS, PAYMENT_TYPE } from 'src/models/PaymentModel';
import PaymentTypeSelector from 'src/components/Control/PaymentTypeSelector';
import MetadataMultiSelector from 'src/components/Control/MetadataMultiSelector';
import OrganisationBankAccountSelector
  from 'src/components/Control/OrganisationBankAccountSelector';

const useStyles = makeStyles((theme) => ({
  datagridQuickSearch: {
    width: '100%',
    border: '1px solid #eeeeee',
    borderTop: 'none',
    padding: theme.spacing(1),
  },
  datagridQuickSearchContainer: {
    maxWidth: '1080px',
    width: '100%',
  },
  datagridQuickSearchClear: {
    color: theme.palette.primary.main,
    '&:Hover': {
      textDecoration: 'underline',
      color: theme.palette.primary.light,
      cursor: 'pointer',
    },
  },
  datagridSearchClose: {
    lineHeight: '20px',
    height: '20px',
    textAlign: 'center',
    margin: 'auto auto',
    color: theme.palette.primary.main,
    '&:hover': {
      color: theme.palette.primary.light,
      cursor: 'pointer',
      textDecoration: 'underline',
    },
  },
  row: {
    display: 'flex',
    alignItems: 'baseline',
    flexDirection: 'row',
    gap: '10px',
    marginTop: '-16px',
  },
  text: {
    padding: theme.spacing(0, 1),
  },
}));

interface Props {
  entity: ENTITIES,
}

const DatagridQuickSearchInvoice = (props: Props) => {
  const classes = useStyles();
  const { entity } = props;

  const dispatch = useAppDispatch();

  const loading = useSelector(
    (state: Store) => (state.formStatus[FORM.query_builder] === FORM_STATUS.loading),
  );
  const lang = useSelector((state: Store) => state.language.language);

  const getDefaultStartDate = () => moment().subtract(12, 'months').startOf('day');

  const getDefaultEndDate = () => moment().add(24, 'months').endOf('day');

  const storedQuickSearch =
    useSelector(
      (state: Store) => (
        state.quickSearch && state.quickSearch[entity] as PaymentQuickSearch
      )
        || {
          startDate: getDefaultStartDate(),
          endDate: getDefaultEndDate(),
        } as PaymentQuickSearch,
    );

  const [paymentQuickSearch, setPaymenteQuickSearch] = useState(storedQuickSearch);

  const getQuickSearch = () => {
    const filters = createPaymentQuickSearchFilter(paymentQuickSearch);
    dispatch(resetSelections({ entity }));
    dispatch(setFilters({
      entity,
      filters,
    } as QuerbuilderPayloadFilters));
    dispatch(setQuickSearch({
      entity,
      quicksearch: paymentQuickSearch,
    } as QuickSearchPayload));
    dispatch(getEntitiesAction(entity));
  };

  const handleKeyDown = (e: { key: string; }) => {
    if (e.key === 'Enter') {
      getQuickSearch();
    }
  };

  const clearFilter = () => {
    dispatch(setFilters({
      entity,
      filters: null,
    } as QuerbuilderPayloadFilters));

    const defaultQuickSearch = {
      startDate: getDefaultStartDate(),
      endDate: getDefaultEndDate(),
      invoiceStatus: [INVOICE_STATUS.final, INVOICE_STATUS.draft, INVOICE_STATUS.error],
    } as PaymentQuickSearch;

    setPaymenteQuickSearch(defaultQuickSearch);

    dispatch(setQuickSearch({
      entity,
      quicksearch: defaultQuickSearch,
    } as QuickSearchPayload));

    dispatch(getEntitiesAction(entity));
  };

  const onChange = (field: string, value:any) => {
    if (field === 'start_date') {
      setPaymenteQuickSearch({
        ...paymentQuickSearch,
        startDate: getDateValue(value, DateType.start),
      });
      return;
    }
    if (field === 'end_date') {
      setPaymenteQuickSearch({
        ...paymentQuickSearch,
        endDate: getDateValue(value, DateType.end),
      });
      return;
    }
    setPaymenteQuickSearch({
      ...paymentQuickSearch,
      [field]: value,
    });
  };

  return (
    <div className={classes.datagridQuickSearch}>
      <div className={classes.datagridQuickSearchContainer}>
        <div className={classes.row} style={{ marginTop: '-25px', height: '48px' }}>
          <div style={{ width: '20%' }}>
            <PaymentStatusSelector
              onChange={onChange}
              values={paymentQuickSearch.paymentStatus || [] as PAYMENT_STATUS[]} />
          </div>
          <div style={{ width: '20%' }}>
            <PaymentTypeSelector
              onChange={onChange}
              values={paymentQuickSearch.paymentType || [] as PAYMENT_TYPE[]} />
          </div>
          <div style={{ width: '20%' }}>
            <OrganisationBankAccountSelector
              style={{ minWidth: '200px' }}
              value={paymentQuickSearch.bankAccountId}
              onChange={(id:number) => onChange('bankAccountId', id)}
              form={FORM.edit_payment}
              name="bankAccountId"
              title="Bank Account"
              margin="dense"
              noMinWidth
              noMarginTop
              disabled={loading} />
          </div>
          <div style={{ width: '20%' }}>
            <MetadataMultiSelector
              form={FORM.payment_search}
              name="accountId"
              onChange={(ids: number[]) => onChange('accountId', ids)}
              type={MetadataType.account}
              margin="dense"
              value={paymentQuickSearch.accountId} />
          </div>
          <div style={{ width: '20%' }}>
            <MetadataMultiSelector
              form={FORM.payment_search}
              name="programId"
              onChange={(ids: number[]) => onChange('programId', ids)}
              type={MetadataType.program}
              margin="dense"
              value={paymentQuickSearch.programId} />
          </div>
          <div style={{ width: '20%' }}>
            <MetadataMultiSelector
              form={FORM.payment_search}
              name="departmentId"
              onChange={(ids: number[]) => onChange('departmentId', ids)}
              type={MetadataType.department}
              margin="dense"
              value={paymentQuickSearch.departmentId} />
          </div>
        </div>
        <div className={classes.row} style={{ marginTop: '3px', height: '66px' }}>
          <div style={{ width: '350px', display: 'flex' }}>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              margin="normal"
              label={t(lang, 'menus.quick_search_term')}
              onChange={(e:any) => onChange('terms', e.target.value as string)}
              onKeyDown={handleKeyDown}
              value={paymentQuickSearch.terms} />
          </div>
          <span className={classes.text}> Between </span>
          <div style={{ width: '110px', display: 'flex' }}>
            <FormDatePicker
              form={FORM.invoice_contact}
              label={t(lang, 'forms.invoices.start_date')}
              name="start_date"
              onChange={
                (date: MaterialUiPickersDate) => onChange('startDate', date)
              }
              value={paymentQuickSearch.startDate} />
          </div>
          <span className={classes.text}> And </span>
          <div style={{ width: '110px', display: 'flex' }}>
            <FormDatePicker
              form={FORM.invoice_contact}
              label={t(lang, 'forms.invoices.end_date')}
              name="end_date"
              onChange={
                (date: MaterialUiPickersDate) => onChange('endDate', date)
              }
              value={paymentQuickSearch.endDate} />
          </div>
          <Button
            variant="contained"
            color="primary"
            style={{
              marginLeft: '8px',
              marginTop: '8px',
            }}
            onClick={getQuickSearch}
            disabled={loading}>
            {t(lang, 'menus.quick_search')}
          </Button>
          <div
            className={classes.datagridQuickSearchClear}
            onClick={() => clearFilter()}>
            {t(lang, 'menus.clear_filter')}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DatagridQuickSearchInvoice;
